import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ChartDataset, ChartOptions } from 'chart.js';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  DeliveryAndShipmentTimes,
  DeliveryTimeGraphService,
  EMPTY_DELIVERY_AND_SHIPMENT_TIMES,
  SortMode,
} from './delivery-time-graph.service';
import {
  BAR_BORDER_RADIUS,
  calculateDisplayParameters,
  createBarDataset,
  createBasicChartOptions,
  CYAN_BAR_COLOR,
  EMPTY_TOOLTIP_DISPLAY_PARAMETERS,
  GREEN_BAR_COLOR,
  TooltipContext,
  TooltipDisplayParameters,
  X_SCALE_OPTIONS,
  Y_SCALE_COMMON_OPTIONS,
} from '../graph-common/graph-base';
import { ResponsiveService } from '@prlw/libs/responsive/responsive.service';

@Component({
  selector: 'prlw-delivery-time-graph',
  templateUrl: './delivery-time-graph.component.html',
  styleUrls: [
    '../graph-common/graphs.scss',
    './delivery-time-graph.component.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeliveryTimeGraphComponent implements OnInit, OnDestroy {
  public readonly CHART_OPTIONS: ChartOptions = {
    ...createBasicChartOptions((args) => this.updateTooltip(args)),
    scales: {
      x: X_SCALE_OPTIONS,
      y: Y_SCALE_COMMON_OPTIONS,
    },
    elements: {
      bar: {
        borderRadius: BAR_BORDER_RADIUS,
      },
    },
  };

  public get datasets(): ChartDataset<'bar', DeliveryAndShipmentTimes[]>[] {
    return [
      this.getDatasetForProperty('shipmentTime', GREEN_BAR_COLOR),
      this.getDatasetForProperty('deliveryTime', CYAN_BAR_COLOR),
    ];
  }

  private getDatasetForProperty(
    property: string,
    barColor: string,
  ): ChartDataset<'bar', DeliveryAndShipmentTimes[]> {
    return createBarDataset({
      xAxisKey: 'basisCode',
      yAxisKey: property,
      barColor,
      chartData: this.chartData ?? [],
    });
  }

  public sortMode: SortMode = null;
  public chartData?: DeliveryAndShipmentTimes[];
  public tooltipData: DeliveryAndShipmentTimes =
    EMPTY_DELIVERY_AND_SHIPMENT_TIMES;
  public tooltipDisplayParams: TooltipDisplayParameters =
    EMPTY_TOOLTIP_DISPLAY_PARAMETERS;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly deliveryTimeGraphService: DeliveryTimeGraphService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly responsiveService: ResponsiveService,
  ) {}

  xsmall$ = this.responsiveService.xsmall$;

  public ngOnInit(): void {
    this.deliveryTimeGraphService.chartData$
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.chartData = data;
        this.changeDetectorRef.detectChanges();
      });

    this.deliveryTimeGraphService.sortMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe((sortMode) => {
        this.sortMode = sortMode;
        this.changeDetectorRef.detectChanges();
      });
    this.deliveryTimeGraphService.setSortMode('shipmentTime');
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // 'bar' is specified to calm down the type checker, in theory 'line' would also work
  private updateTooltip(context: TooltipContext<'bar'>): void {
    const dataPoint = context.tooltip.dataPoints[0]
      .raw as DeliveryAndShipmentTimes;
    this.tooltipData = this.getTooltipValues(dataPoint);
    this.tooltipDisplayParams = calculateDisplayParameters(context);
    if (this.xsmall$.getValue()) {
      this.tooltipDisplayParams = EMPTY_TOOLTIP_DISPLAY_PARAMETERS;
    }
    this.changeDetectorRef.detectChanges();
  }

  private getTooltipValues(
    dataPoint: DeliveryAndShipmentTimes,
  ): DeliveryAndShipmentTimes {
    return (
      (this.chartData ?? []).find(
        (data) => data.basisCode === dataPoint.basisCode,
      ) ?? EMPTY_DELIVERY_AND_SHIPMENT_TIMES
    );
  }
}
